/**
 * Image Format Converter Service
 * Convert images between different formats
 */

export type ImageFormat = 'png' | 'jpeg' | 'webp' | 'gif' | 'bmp' | 'ico';

export interface ConversionResult {
  blob: Blob;
  dataUrl: string;
  format: ImageFormat;
  width: number;
  height: number;
  size: number;
}

export interface ConversionOptions {
  format: ImageFormat;
  quality?: number; // 0-1, for lossy formats
  width?: number;
  height?: number;
  maintainAspectRatio?: boolean;
  backgroundColor?: string; // For formats without transparency
}

export const FORMAT_INFO: Record<ImageFormat, { 
  name: string; 
  mime: string; 
  extension: string;
  supportsTransparency: boolean;
  lossy: boolean;
}> = {
  png: { name: 'PNG', mime: 'image/png', extension: '.png', supportsTransparency: true, lossy: false },
  jpeg: { name: 'JPEG', mime: 'image/jpeg', extension: '.jpg', supportsTransparency: false, lossy: true },
  webp: { name: 'WebP', mime: 'image/webp', extension: '.webp', supportsTransparency: true, lossy: true },
  gif: { name: 'GIF', mime: 'image/gif', extension: '.gif', supportsTransparency: true, lossy: false },
  bmp: { name: 'BMP', mime: 'image/bmp', extension: '.bmp', supportsTransparency: false, lossy: false },
  ico: { name: 'ICO', mime: 'image/x-icon', extension: '.ico', supportsTransparency: true, lossy: false },
};

/**
 * Convert image to different format
 */
export async function convertImageFormat(
  file: File,
  options: ConversionOptions
): Promise<ConversionResult> {
  const { 
    format, 
    quality = 0.92, 
    width, 
    height, 
    maintainAspectRatio = true,
    backgroundColor = '#ffffff'
  } = options;
  
  const img = await loadImage(file);
  
  // Calculate dimensions
  let newWidth = width || img.width;
  let newHeight = height || img.height;
  
  if (maintainAspectRatio && (width || height)) {
    if (width && !height) {
      newHeight = Math.round((img.height * width) / img.width);
    } else if (height && !width) {
      newWidth = Math.round((img.width * height) / img.height);
    } else if (width && height) {
      const ratio = Math.min(width / img.width, height / img.height);
      newWidth = Math.round(img.width * ratio);
      newHeight = Math.round(img.height * ratio);
    }
  }
  
  // Special handling for ICO
  if (format === 'ico') {
    return createIco(img, newWidth, newHeight);
  }
  
  // Create canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Canvas not supported');
  
  canvas.width = newWidth;
  canvas.height = newHeight;
  
  // Fill background for formats without transparency
  const formatInfo = FORMAT_INFO[format];
  if (!formatInfo.supportsTransparency) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, newWidth, newHeight);
  }
  
  // Enable high-quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Draw image
  ctx.drawImage(img, 0, 0, newWidth, newHeight);
  
  // Get blob
  const mimeType = formatInfo.mime;
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to convert image'));
      },
      mimeType,
      formatInfo.lossy ? quality : undefined
    );
  });
  
  return {
    blob,
    dataUrl: await blobToDataUrl(blob),
    format,
    width: newWidth,
    height: newHeight,
    size: blob.size,
  };
}

/**
 * Create ICO file from image
 */
async function createIco(
  img: HTMLImageElement, 
  _width: number, 
  _height: number
): Promise<ConversionResult> {
  // ICO standard sizes
  const sizes = [16, 32, 48];
  const images: Uint8Array[] = [];
  
  for (const size of sizes) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    
    canvas.width = size;
    canvas.height = size;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Center crop for square
    const srcSize = Math.min(img.width, img.height);
    const srcX = (img.width - srcSize) / 2;
    const srcY = (img.height - srcSize) / 2;
    
    ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, size, size);
    
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => b ? resolve(b) : reject(), 'image/png');
    });
    
    const arrayBuffer = await blob.arrayBuffer();
    images.push(new Uint8Array(arrayBuffer));
  }
  
  // Create ICO structure
  const icoBlob = createIcoBlob(images, sizes);
  
  return {
    blob: icoBlob,
    dataUrl: await blobToDataUrl(icoBlob),
    format: 'ico',
    width: 48,
    height: 48,
    size: icoBlob.size,
  };
}

/**
 * Create ICO blob from PNG data
 */
function createIcoBlob(images: Uint8Array[], sizes: number[]): Blob {
  const headerSize = 6;
  const entrySize = 16;
  const entriesSize = entrySize * images.length;
  const dataOffset = headerSize + entriesSize;
  
  let totalDataSize = 0;
  for (const img of images) {
    totalDataSize += img.length;
  }
  
  const buffer = new ArrayBuffer(dataOffset + totalDataSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  
  // ICONDIR header
  view.setUint16(0, 0, true);
  view.setUint16(2, 1, true);
  view.setUint16(4, images.length, true);
  
  let currentOffset = dataOffset;
  for (let i = 0; i < images.length; i++) {
    const entryOffset = headerSize + (i * entrySize);
    const size = sizes[i];
    
    view.setUint8(entryOffset + 0, size === 256 ? 0 : size);
    view.setUint8(entryOffset + 1, size === 256 ? 0 : size);
    view.setUint8(entryOffset + 2, 0);
    view.setUint8(entryOffset + 3, 0);
    view.setUint16(entryOffset + 4, 1, true);
    view.setUint16(entryOffset + 6, 32, true);
    view.setUint32(entryOffset + 8, images[i].length, true);
    view.setUint32(entryOffset + 12, currentOffset, true);
    
    bytes.set(images[i], currentOffset);
    currentOffset += images[i].length;
  }
  
  return new Blob([bytes], { type: 'image/x-icon' });
}

/**
 * Load image from file
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to convert blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Get supported input formats
 */
export function getSupportedInputFormats(): string[] {
  return ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp', 'image/svg+xml'];
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
